const CLICK_VOLUME = 0.05;
const CLICK_DURATION_SECONDS = 0.08;
const CLICK_ATTACK_SECONDS = 0.01;
const CLICK_DECAY_EXPONENT = 8;
const CLICK_PLAYBACK_RATE = 2;

let audio: AudioContext | null = null;
let clickBuffer: AudioBuffer | null = null;

const createClickBuffer = (context: AudioContext) => {
  const length = Math.ceil(context.sampleRate * CLICK_DURATION_SECONDS);
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const samples = buffer.getChannelData(0);

  for (let i = 0; i < length; i++) {
    const attack = Math.min(1, i / (context.sampleRate * CLICK_ATTACK_SECONDS));
    const decay = (1 - i / length) ** CLICK_DECAY_EXPONENT;
    samples[i] = (Math.random() * 2 - 1) * attack * decay;
  }

  return buffer;
};

export const initializeAudio = () => {
  if (typeof AudioContext === 'undefined') return;

  const removeListeners = () => {
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('pointerup', unlock, true);
    window.removeEventListener('keydown', unlock, true);
  };

  const unlock = () => {
    const context = audio ?? new AudioContext();
    audio = context;
    clickBuffer ??= createClickBuffer(context);
    void context
      .resume()
      .then(() => {
        if (audio === context && context.state === 'running') removeListeners();
      })
      .catch((error: unknown) => {
        console.warn('Could not enable audio:', error);
      });
  };

  window.addEventListener('pointerdown', unlock, {
    capture: true,
    passive: true,
  });
  window.addEventListener('pointerup', unlock, {
    capture: true,
    passive: true,
  });
  window.addEventListener('keydown', unlock, true);

  return () => {
    removeListeners();
    void audio?.close().catch(() => undefined);
    audio = null;
    clickBuffer = null;
  };
};

export const playClick = () => {
  const context = audio;
  if (context?.state !== 'running' || !clickBuffer) return;

  const sound = context.createBufferSource();
  const gain = context.createGain();
  sound.buffer = clickBuffer;
  sound.playbackRate.value = CLICK_PLAYBACK_RATE;
  gain.gain.value = CLICK_VOLUME;
  sound.connect(gain);
  gain.connect(context.destination);
  sound.onended = () => {
    sound.disconnect();
    gain.disconnect();
  };
  sound.start();
};
